import { buildSpotlights, eligibleSpotlight } from "./spotlight-catalog.js";

const ACCENT = "#78baff"; // attention only; distinct from the five domain colours
const SEEN_KEY = "entourages.discoverySeen.v1";
const TIP_KEY = "entourages.discoveryTipSeen.v1";

export function installDiscovery({ graph, renderer, container, mobile, initialView, getState, refresh, labels, showEdge }) {
  const button = document.querySelector("#surpriseButton");
  const mobileButton = document.querySelector("#mobileSurpriseButton");
  const card = document.querySelector("#discoveryCard");
  const canvas = document.createElement("canvas");
  canvas.className = "discovery-halo";
  container.append(canvas);
  const context = canvas.getContext("2d");
  let catalogue = [], active = null, nodes = new Set(), edges = new Set();
  let seen = new Set(), timer = null, stepTimer = null, haloFrame = null;
  let cancelled = false, automaticCount = 0, haloStart = 0, lastKind = null;
  let tipSeen = false;
  try { tipSeen = localStorage.getItem(TIP_KEY) === "1"; } catch { /* private storage */ }
  const reduced = matchMedia("(prefers-reduced-motion: reduce)");
  try { if (sessionStorage.getItem(SEEN_KEY)) seen = new Set(JSON.parse(sessionStorage.getItem(SEEN_KEY))); } catch { /* private storage */ }
  const saveSeen = () => { try { sessionStorage.setItem(SEEN_KEY, JSON.stringify([...seen])); } catch { /* private storage */ } };
  const dispatch = (name, detail = {}) => document.dispatchEvent(new CustomEvent(name, { detail }));
  const clearTimers = () => { clearTimeout(timer); clearTimeout(stepTimer); timer = stepTimer = null; cancelAnimationFrame(haloFrame); haloFrame = null; context.clearRect(0, 0, canvas.width, canvas.height); };
  function dismiss(reason = "dismissed") {
    clearTimers();
    if (!active) return;
    active = null; nodes = new Set(); edges = new Set();
    card.hidden = true; labels.setSpotlight(null); refresh();
    dispatch("discovery_spotlight_dismissed", { reason });
  }
  function takeControl() {
    if (!cancelled) dispatch("discovery_user_took_control");
    cancelled = true;
    // Sigma Camera has no public stop method: an identity animation replaces its pending frame.
    if (renderer.getCamera().isAnimated()) renderer.getCamera().animate(renderer.getCamera().getState(), { duration: 1 });
    dismiss("user");
  }
  function valid(spotlight) {
    const state = getState();
    return !state.pathIds.length && !state.selectedNode && !state.selectedEdge && !state.domains.size &&
      eligibleSpotlight(spotlight, graph, state.relationType) && (mobile.matches ? spotlight.nodeIds.length <= 4 : true) &&
      spotlight.nodeIds.every(id => {
        const { x, y } = graph.getNodeAttributes(id);
        return Number.isFinite(x) && Number.isFinite(y);
      });
  }
  function frameFit(item) {
    const points = item.nodeIds.map(id => renderer.graphToViewport(graph.getNodeAttributes(id)));
    const xs = points.map(p => p.x), ys = points.map(p => p.y);
    const { width, height } = renderer.getDimensions();
    return Math.max((Math.max(...xs) - Math.min(...xs) + 120) / Math.max(100, width - (mobile.matches ? 88 : 180)),
      (Math.max(...ys) - Math.min(...ys) + 110) / Math.max(100, height - (mobile.matches ? 235 : 145)));
  }
  function choose() {
    // The displayed component can grow after the initial layout becomes ready.
    catalogue = buildSpotlights(graph);
    let available = catalogue.filter(valid);
    if (!available.length) return null;
    if (available.every(item => seen.has(item.id))) { seen.clear(); saveSeen(); }
    available = available.filter(item => !seen.has(item.id));
    // Rotate families, not just IDs, while keeping selection deterministic.
    const kinds = ["RELATION", "TRIANGLE", "PATH", "EGO", "BRIDGE"];
    const start = lastKind ? (kinds.indexOf(lastKind) + 1) % kinds.length : 0;
    for (let i = 0; i < kinds.length; i++) {
      const group = available.filter(item => item.kind === kinds[(start + i) % kinds.length]);
      if (group.length) return group[seen.size % group.length];
    }
    return available[0];
  }
  function frame(item) {
    const points = item.nodeIds.map(id => renderer.graphToViewport(graph.getNodeAttributes(id)));
    const xs = points.map(p => p.x), ys = points.map(p => p.y);
    const { width, height } = renderer.getDimensions();
    const current = renderer.getCamera().getState();
    const middle = { x: (Math.min(...xs) + Math.max(...xs)) / 2,
      y: (Math.min(...ys) + Math.max(...ys)) / 2 + (mobile.matches ? 26 : 0) };
    const center = renderer.viewportToFramedGraph(middle);
    const fit = frameFit(item);
    // A discovery is navigational: fit its evidence even when the current
    // camera is zoomed far in or the entire graph is compressed on screen.
    const ratio = Math.max(.09, Math.min(30, current.ratio * fit));
    const view = { x: center.x, y: center.y, ratio, angle: current.angle };
    if (reduced.matches) renderer.getCamera().setState(view);
    else renderer.getCamera().animate(view, { duration: 680, easing: "quadraticInOut" });
  }
  function halo(now) {
    if (!active || reduced.matches) return;
    const elapsed = Math.max(0, now - haloStart);
    const { width, height } = renderer.getDimensions(), scale = devicePixelRatio || 1;
    if (canvas.width !== Math.round(width * scale) || canvas.height !== Math.round(height * scale)) {
      canvas.width = Math.round(width * scale); canvas.height = Math.round(height * scale);
    }
    context.setTransform(scale, 0, 0, scale, 0, 0);
    context.clearRect(0, 0, width, height);
    if (elapsed > 680) return;
    const progress = elapsed / 680;
    context.strokeStyle = `rgba(120,186,255,${(.42 * (1 - progress)).toFixed(3)})`;
    context.lineWidth = 1.5;
    for (const id of active.nodeIds) {
      const p = renderer.graphToViewport(graph.getNodeAttributes(id));
      context.beginPath(); context.arc(p.x, p.y, 11 + progress * 16, 0, Math.PI * 2); context.stroke();
    }
    haloFrame = requestAnimationFrame(halo);
  }
  function reveal(item) {
    nodes = new Set(item.nodeIds); edges = new Set(item.edgeIds);
    labels.setSpotlight(nodes); refresh();
    haloStart = performance.now();
    if (!reduced.matches) haloFrame = requestAnimationFrame(halo);
  }
  function activate(item, automatic = false) {
    if (!item) return false;
    dismiss("replaced");
    active = item; seen.add(item.id); saveSeen();
    lastKind = item.kind;
    card.querySelector("#discoveryTitle").textContent = item.title;
    card.querySelector("#discoverySubtitle").textContent = item.subtitle;
    const tip = card.querySelector("#discoveryTip");
    tip.hidden = tipSeen;
    if (!tipSeen) {
      tip.textContent = mobile.matches
        ? "Touchez une personne pour explorer ses liens, ou continuez avec « Une autre découverte »."
        : "Cliquez sur une personne pour explorer ses liens, ou continuez avec « Une autre découverte ».";
      tipSeen = true;
      try { localStorage.setItem(TIP_KEY, "1"); } catch { /* private storage */ }
    }
    card.querySelector("#discoveryRelation").hidden = item.edgeIds.length !== 1;
    card.hidden = false;
    frame(item);
    if (item.kind === "PATH" && !reduced.matches) {
      let index = 0;
      const step = () => {
        if (active !== item) return;
        nodes = new Set(item.nodeIds.slice(0, index + 1)); edges = new Set(item.edgeIds.slice(0, index));
        labels.setSpotlight(nodes); refresh();
        index++;
        if (index < item.nodeIds.length) stepTimer = setTimeout(step, 240);
        else { haloStart = performance.now(); haloFrame = requestAnimationFrame(halo); }
      };
      step();
    } else reveal(item);
    dispatch("discovery_spotlight_shown", { kind: item.kind, automatic });
    return true;
  }
  function scheduleAutomatic() {
    if (cancelled || automaticCount >= 2) return;
    timer = setTimeout(() => {
      if (cancelled || !validForAutomatic()) return;
      if (activate(choose(), true)) {
        try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* private storage */ }
        automaticCount++;
        if (automaticCount < 2) scheduleAutomatic();
      }
    }, automaticCount ? 7000 : 800);
  }
  function validForAutomatic() {
    const state = getState();
    return !state.pathIds.length && !state.selectedNode && !state.selectedEdge && !state.domains.size && state.relationType === "ALL";
  }
  function ready() {
    catalogue = buildSpotlights(graph);
    const enabled = catalogue.length > 0;
    button.disabled = mobileButton.disabled = !enabled;
    if (!enabled) return;
    // Read the original URL; app URL serialization writes a camera even for root visits.
    const explicit = new URLSearchParams(initialView);
    const deepLink = [...explicit.keys()].some(key => key !== "utm_source" && key !== "utm_medium" && key !== "utm_campaign");
    let previouslySeen = false;
    try { previouslySeen = localStorage.getItem(SEEN_KEY) === "1"; } catch { /* private storage */ }
    if (!deepLink && !previouslySeen && !cancelled && validForAutomatic()) {
      // Atlas overview stays spatially persistent; discoveries remain user-triggered.
      cancelled = true;
    }
  }
  const surprise = () => {
    clearTimeout(timer); cancelled = true;
    dispatch("discovery_surprise_clicked");
    if (!activate(choose())) {
      const toast = document.querySelector("#toast"); toast.textContent = "Aucune découverte compatible avec la vue actuelle";
      toast.classList.add("is-visible"); setTimeout(() => toast.classList.remove("is-visible"), 1800);
    }
  };
  button.addEventListener("click", surprise); mobileButton.addEventListener("click", surprise);
  card.querySelector("#discoveryAnother").addEventListener("click", surprise);
  card.querySelector("#discoveryClose").addEventListener("click", takeControl);
  card.querySelector("#discoveryRelation").addEventListener("click", () => {
    if (!active?.edgeIds[0]) return;
    const id = active.edgeIds[0]; dispatch("discovery_spotlight_opened", { kind: active.kind });
    takeControl(); void showEdge(id);
  });
  return { ready, takeControl, dismiss, state: () => ({ active, nodes, edges, color: ACCENT }),
    invalidate: () => { if (active && !valid(active)) dismiss("invalid"); },
    destroy: () => { clearTimers(); canvas.remove(); } };
}
